import type { ErrorCode } from './types';

/**
 * Every failure the client surfaces — transport, malformed response, or a real
 * error envelope — arrives at the UI as one of these, so the render path has a
 * single shape to switch on and can never end up printing a status code.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  /**
   * The server's prose, kept for diagnostics and for the one case where it
   * carries information our own copy cannot: `list_too_small` says how many
   * matches were found. Never rendered on its own.
   */
  readonly serverMessage: string | null;
  /** Parsed from the `Retry-After` header on 429. Seconds. */
  readonly retryAfterSeconds: number | null;

  constructor(
    code: ErrorCode,
    options: { serverMessage?: string | null; retryAfterSeconds?: number | null } = {},
  ) {
    super(options.serverMessage ?? code);
    this.name = 'ApiError';
    this.code = code;
    this.serverMessage = options.serverMessage ?? null;
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
